import {
  PDF_CLOSING_LOGO_CLASS,
  PDF_CLOSING_LOGO_WIDTH_PX,
  PDF_QUOTATION_LOGO_CLASS,
  PDF_QUOTATION_LOGO_WIDTH_PX,
} from "@/lib/proposalPdfQuotationLayout";

const DEFAULT_FONT_READY_TIMEOUT_MS = 5000;
const DEFAULT_IMAGE_PRELOAD_TIMEOUT_MS = 8000;
const LAYOUT_SETTLE_MS = 64;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function hasValidImageSrc(img: HTMLImageElement): boolean {
  const raw = (img.getAttribute("src") ?? img.currentSrc ?? "").trim();
  if (!raw) return false;
  if (raw === "#" || raw === "about:blank") return false;
  return true;
}

function isCrossOriginUrl(src: string): boolean {
  try {
    const url = new URL(src, window.location.href);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function hideBrokenImage(img: HTMLImageElement): void {
  img.style.visibility = "hidden";
  img.style.opacity = "0";
  img.setAttribute("data-pdf-image-failed", "true");
  img.removeAttribute("src");
}

function waitForSingleImage(img: HTMLImageElement): Promise<void> {
  return new Promise((resolve) => {
    const finalize = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        hideBrokenImage(img);
      }
      resolve();
    };

    if (img.complete) {
      finalize();
      return;
    }

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      img.removeEventListener("load", done);
      img.removeEventListener("error", onError);
      finalize();
    };
    const onError = () => {
      hideBrokenImage(img);
      done();
    };

    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", onError, { once: true });
  });
}

async function prepareImageElement(img: HTMLImageElement): Promise<void> {
  const src = (img.getAttribute("src") ?? img.currentSrc ?? "").trim();
  if (!src || !hasValidImageSrc(img)) return;

  img.loading = "eager";
  img.decoding = "sync";

  if (isCrossOriginUrl(src)) {
    const needsReload = img.crossOrigin !== "anonymous";
    img.crossOrigin = "anonymous";
    if (needsReload && img.complete) {
      const current = img.currentSrc || src;
      img.removeAttribute("src");
      img.src = current;
    }
  }

  await waitForSingleImage(img);
}

/**
 * Convert header/closing SVG logos to PNG data URLs so html2canvas paints them reliably.
 * Does not touch cover (cover has no logo).
 */
export async function rasterizeProposalPdfLogos(root: HTMLElement): Promise<void> {
  const logos = [
    ...root.querySelectorAll<HTMLImageElement>(
      `.${PDF_QUOTATION_LOGO_CLASS}, .${PDF_CLOSING_LOGO_CLASS}, .pdf-print-page-logo, [data-testid="dtools-pdf-quotation-logo"], [data-testid="dtools-pdf-closing-logo"]`,
    ),
  ];
  for (const img of logos) {
    const src = (img.getAttribute("src") ?? img.currentSrc ?? "").trim();
    if (!src || src.startsWith("data:image/png")) continue;
    await prepareImageElement(img);
    if (img.getAttribute("data-pdf-image-failed") === "true") continue;
    if (img.naturalWidth < 1 || img.naturalHeight < 1) continue;

    const isClosing =
      img.classList.contains(PDF_CLOSING_LOGO_CLASS) ||
      img.getAttribute("data-testid") === "dtools-pdf-closing-logo";
    const targetW = (isClosing ? PDF_CLOSING_LOGO_WIDTH_PX : PDF_QUOTATION_LOGO_WIDTH_PX) * 2;
    const targetH = Math.max(1, Math.round((targetW * img.naturalHeight) / img.naturalWidth));
    try {
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.clearRect(0, 0, targetW, targetH);
      ctx.drawImage(img, 0, 0, targetW, targetH);
      const dataUrl = canvas.toDataURL("image/png");
      if (!dataUrl || dataUrl.length < 64) continue;
      img.src = dataUrl;
      img.removeAttribute("crossorigin");
      await waitForSingleImage(img);
      img.setAttribute("data-pdf-logo-rasterized", "true");
    } catch {
      // Keep original asset if canvas is tainted or draw fails.
    }
  }
}

/** Wait for web fonts before rasterizing — prevents fallback-font captures. */
export async function waitForProposalPdfFonts(timeoutMs = DEFAULT_FONT_READY_TIMEOUT_MS): Promise<void> {
  if (typeof document === "undefined") return;
  try {
    if (document.fonts?.ready) {
      await Promise.race([document.fonts.ready, delay(timeoutMs)]);
    }
  } catch {
    // FontFaceSet may throw in older browsers — continue with system fonts.
  }
  await delay(LAYOUT_SETTLE_MS);
}

/** Preload, CORS-tag, and gracefully hide images that fail before html2canvas runs. */
export async function prepareProposalPdfImages(
  root: ParentNode,
  timeoutMs = DEFAULT_IMAGE_PRELOAD_TIMEOUT_MS,
): Promise<void> {
  const images = [...root.querySelectorAll<HTMLImageElement>("img")].filter(hasValidImageSrc);
  if (images.length === 0) return;

  await Promise.race([Promise.all(images.map(prepareImageElement)), delay(timeoutMs)]);
}

/** Release canvas memory after each exported page. */
export function releaseExportCanvas(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d");
  ctx?.clearRect(0, 0, canvas.width, canvas.height);
  canvas.width = 0;
  canvas.height = 0;
}

export async function prepareProposalPdfForExport(root: HTMLElement): Promise<void> {
  await waitForProposalPdfFonts();
  const { ensureProposalPdfQrImages } = await import("@/lib/proposalPdfExportCanvas");
  await ensureProposalPdfQrImages(root);
  await prepareProposalPdfImages(root);
  await rasterizeProposalPdfLogos(root);
  await delay(LAYOUT_SETTLE_MS);
}
